import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { useTranslation } from '@/components/utils/translations';
import { MARKET_FIT_DISTRIBUTION_BANDS as bands } from '@/lib/marketFitDistributionScale';

export default function MarketFitGrid({ accounts, isLoading }) {
  const { t } = useTranslation();
  const [selectedBand, setSelectedBand] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getCountInBand = (min, max) => accounts.filter(a => a.account_fit_score >= min && a.account_fit_score <= max).length;

  const handleBandClick = (band) => {
    setSelectedBand(band);
    setIsModalOpen(true);
  };
  
  const selectedAccounts = selectedBand ? accounts.filter(a => {
      const [min, max] = selectedBand.range.split('-').map(Number);
      return a.account_fit_score >= min && a.account_fit_score <= max;
  }) : [];

  return (
    <>
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#0A1A2F]">{t('marketFitDistribution')}</CardTitle>
          <p className="text-sm text-[#5C6B7A]">{t('accountFitComposition')}</p>
        </CardHeader>
        <CardContent>
          {isLoading ? <Skeleton className="h-16 w-full" /> : (
          <div className="flex flex-wrap -m-1">
            {bands.map(band => {
              const count = getCountInBand(...band.range.split('-').map(Number));
              return (
                <button 
                  key={band.range} 
                  style={{ backgroundColor: band.color, color: band.textColor }} 
                  className="m-1 flex-1 p-3 rounded-md text-center min-w-[80px] hover:shadow-lg transition-shadow cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => handleBandClick(band)}
                  disabled={count === 0}
                >
                  <div className="font-bold text-xl">{count}</div>
                  <div className="text-xs opacity-80">{band.range}</div>
                </button>
              );
            })}
          </div>
          )}
        </CardContent>
      </Card>
      
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
            <DialogHeader>
                <DialogTitle>Accounts in Fit Score Band: {selectedBand?.range}</DialogTitle>
                <DialogDescription>
                    List of accounts with a fit score between {selectedBand?.range.split('-')[0]} and {selectedBand?.range.split('-')[1]}.
                </DialogDescription>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-2">
                {selectedAccounts.map(acc => (
                    <Link to={createPageUrl(`AccountFinal?id=${acc.id}`)} key={acc.id} className="flex items-center gap-3 p-2 rounded-md hover:bg-gray-100">
                        {acc.logo_url ? (
                            <img src={acc.logo_url} alt={acc.account_name} className="w-8 h-8 object-contain rounded-md border p-0.5 bg-white" />
                        ) : (
                            <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-md">
                                <Building2 className="w-4 h-4 text-[#5C6B7A]" />
                            </div>
                        )}
                        <div className="flex-1">
                            <p className="text-sm font-medium text-[#0A1A2F]">{acc.account_name}</p>
                            <p className="text-xs text-[#5C6B7A]">{acc.country} • Score: {acc.account_fit_score}</p>
                        </div>
                    </Link>
                ))}
            </div>
        </DialogContent>
      </Dialog>
    </>
  );
}